import React, { useEffect, useState } from 'react';
import NextHead from 'next/head';
import classNames from 'classnames';
import { Container, Anchor } from 'components/base';
import { CloudinaryPicture, ScriptMarkupFaq, WIDTH_LADDERS } from 'components/rich';
import { serializeJsonLd } from 'components/rich/serializeJsonLd';

import { Head, Layout } from '../../components/layout';
import PdpIcon from '../../components/product/pdpIcon';
import ProductFaq from '../../components/product/productFaq';
import tracker from '../../lib/tracker';
import SETTINGS from '../../settings';

const APP_STORE_URL = new URL(SETTINGS.APP_STORE_URL);
APP_STORE_URL.searchParams.set('utm_source', 'website');
APP_STORE_URL.searchParams.set('utm_medium', 'guide');
APP_STORE_URL.searchParams.set('utm_campaign', 'print_from_iphone');

const PAGE_PATH = '/guides/print-photos-from-iphone';

const DESTINATIONS = {
  app_store: APP_STORE_URL.href,
  size_tool: '/tools/photo-print-sizes',
  'square-prints': '/square-prints',
  'classic-prints': '/classic-prints',
  'engineer-prints': '/engineer-prints',
};

const trackCtaClick = (cta, location) => () => {
  tracker.onCustomEvent('print_from_iphone_cta_click', { cta, location });
};

const ctaLink = (cta, label, location, className = 'pfi-text-link') => (
  <Anchor
    href={DESTINATIONS[cta]}
    className={className}
    targetBlank={cta === 'app_store'}
    onClick={trackCtaClick(cta, location)}
  >
    {label}
  </Anchor>
);

const arrowLink = (cta, label, location) =>
  ctaLink(
    cta,
    <>
      {label}
      <PdpIcon name="arrowRight" size={15} />
    </>,
    location,
    'pfi-arrow-link',
  );

const appStoreBadge = (location) =>
  ctaLink(
    'app_store',
    <img
      src="/static/img/app-store-badge.svg"
      alt="Download Parabo Press on the App Store"
      width="120"
      height="40"
    />,
    location,
    'link-app-store',
  );

// Ids are jump-link targets. `how-to-order` and `choose-your-size` predate the redesign.
const SECTIONS = [
  { id: 'short-answer', label: 'Short answer' },
  { id: 'why-print', label: 'Why print from iPhone' },
  { id: 'airprint-vs-lab', label: 'AirPrint vs a photo lab' },
  { id: 'how-to-order', label: 'Step-by-step' },
  { id: 'choose-your-size', label: 'Which size' },
  { id: 'heic-quality', label: 'HEIC and quality' },
  { id: 'square-vs-classic', label: 'Square vs Classic' },
  { id: 'big-print', label: 'One big print' },
  { id: 'faq', label: 'FAQ' },
];

const HERO_FACTS = [
  { icon: 'listOrdered', label: '4 steps' },
  { icon: 'printer', label: 'No printer needed' },
  { icon: 'check', label: 'HEIC, JPG or PNG' },
];

const AIRPRINT_ROWS = [
  { label: 'You need', airprint: 'A compatible printer, paper and ink', lab: 'Your iPhone' },
  { label: 'Where it prints', airprint: 'At home', lab: 'At the lab' },
  { label: 'You get', airprint: 'Prints from your own printer', lab: 'Finished prints, delivered' },
];

const STEPS = [
  {
    id: 'choose',
    title: 'Choose your photos',
    body: 'Start with a few favorites from your Camera Roll. Use the original photos whenever possible, rather than screenshots or copies saved from social media.',
  },
  {
    id: 'size',
    title: 'Pick a print and size',
    body: 'Choose Square Prints for a square crop or Classic Prints for a rectangle. Think about where the prints will live, from a journal to a frame on your shelf.',
  },
  {
    id: 'border',
    title: 'Check the crop and border',
    body: 'Review each photo in the print shape you chose. Keep faces and other details inside the crop, then choose a white border or an edge-to-edge print.',
  },
  {
    id: 'order',
    title: 'Review and order',
    body: 'Check your photos, size and quantity before checkout. Add your delivery details and place your order. Parabo prints your photos and ships them to you.',
  },
];

// Shape icons are drawn roughly to relative scale (px per side).
const SIZES = [
  { size: '2.75×2.75 in', product: 'Square Prints', use: 'Journals and gift tags', shape: [16, 16] },
  { size: '4×4 in', product: 'Square Prints', use: 'A desk or a gift stack', shape: [20, 20] },
  { size: '4×6 in', product: 'Classic Prints', use: 'A desk or a gift stack', shape: [14, 20] },
  { size: '5.5×5.5 in', product: 'Square Prints', use: 'A little more presence', shape: [26, 26] },
  { size: '5×7 in', product: 'Classic Prints', use: 'A little more presence', shape: [19, 26] },
];

const USE_LIST = [
  'The original photo from your Camera Roll',
  'HEIC, JPG or PNG',
  'The biggest version you have',
];
const AVOID_LIST = ['Screenshots', 'Social-media downloads', 'Tight crops of a small photo'];

const PPI_TARGETS = [
  { value: '150+ PPI', label: 'Aim for this at the printed size. Square and Classic Prints.' },
  { value: '~300 PPI', label: 'Aim for this at the printed size. Engineer Prints (3×4 ft).' },
];

const PRODUCTS = [
  {
    id: 'square-prints',
    title: 'Square Prints',
    sizes: '2.75×2.75, 4×4 or 5.5×5.5 inches',
    body: 'A consistent shape for a photo wall, journals and little gifts.',
    cta: 'Order square prints',
    imageKey: 'square-prints/2',
  },
  {
    id: 'classic-prints',
    title: 'Classic Prints',
    sizes: '4×6 or 5×7 inches',
    body: 'Familiar rectangles for albums, simple frames and tabletop displays.',
    cta: 'Order classic prints',
    imageKey: 'classic-prints/1',
  },
];

const FAQ = [
  {
    question: 'Do I need a printer to print photos from my iPhone?',
    answer: 'No. With the Parabo Press app, you order photo prints from your phone. Parabo prints them and ships them to you. You do not need a home printer or AirPrint.',
  },
  {
    question: 'Can I use HEIC photos?',
    answer: 'Parabo accepts HEIC, JPG and PNG photos. Start with the original file and review the crop before ordering. The file format alone does not tell you how sharp a print will look.',
  },
  {
    question: 'Which size should I choose for iPhone photos?',
    answer: 'Choose a size for the place you want to display it. Square Prints come in 2.75×2.75, 4×4 and 5.5×5.5 inches. Classic Prints come in 4×6 and 5×7 inches. Review the crop because a different print shape can leave out part of your photo.',
  },
  {
    question: 'Will my photo be sharp enough?',
    answer: 'Sharpness depends on the original photo, its pixel dimensions, the crop and the print size. For Square and Classic Prints, aim for 150+ PPI at the printed size. A smaller print or a higher-resolution original can help when a file is too small.',
  },
  {
    question: 'Can I make a large print from an iPhone photo?',
    answer: 'It depends on the file. A 3×4 ft Engineer Print needs a strong, high-resolution original. Aim for about 300 PPI for the best results. If the file is too small or heavily cropped, choose a smaller Square or Classic Print.',
  },
];

// Same questions and answers as the FAQPage JSON-LD, in the shape ProductFaq renders.
const FAQ_ITEMS = FAQ.map(({ question, answer }) => ({ caption: question, description: answer }));

const BREADCRUMB_JSON_LD = serializeJsonLd({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${SETTINGS.HOST}/` },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Print photos from iPhone',
      item: `${SETTINGS.HOST}${PAGE_PATH}`,
    },
  ],
});

// Highlights the section currently at the top of the viewport in the sticky menu.
const useActiveSection = () => {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: '0px 0px -75% 0px' },
    );
    SECTIONS.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);

  return activeId;
};

const PrintPhotosFromIphonePage = () => {
  const activeId = useActiveSection();

  return (
    <>
      <Head
        title="How to Print Photos from iPhone | Camera Roll to Real Prints"
        description="Learn how to print photos from iPhone the right way, from Camera Roll to lab-quality Square and Classic prints (not AirPrint). Free size guide included."
        image={`${SETTINGS.HOST}/seo/products/square-prints.jpg`}
      />
      <ScriptMarkupFaq items={FAQ} />
      <NextHead>
        <script
          key="ScriptMarkupBreadcrumb"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: BREADCRUMB_JSON_LD }}
        />
      </NextHead>
      <Layout>
        <article className="pfi">
          <section className="pfi-hero">
            <Container>
              <div className="pfi-hero-inner">
                <div className="pfi-hero-copy">
                  <nav aria-label="Breadcrumb">
                    <ol className="pfi-breadcrumb">
                      <li>
                        <Anchor href="/">Home</Anchor>
                        <PdpIcon name="chevronRight" size={12} />
                      </li>
                      <li aria-current="page">Print photos from iPhone</li>
                    </ol>
                  </nav>
                  <p className="pfi-eyebrow">From Camera Roll to real prints</p>
                  <h1 className="pfi-h1">How to print photos from your iPhone</h1>
                  <p className="pfi-lead">
                    Your best photos are already on your phone. Here’s how to turn them into real
                    prints, shipped to you, without fighting AirPrint or a home printer.
                  </p>
                  <ul className="pfi-facts">
                    {HERO_FACTS.map(({ icon, label }) => (
                      <li key={label}>
                        <PdpIcon name={icon} size={16} />
                        {label}
                      </li>
                    ))}
                  </ul>
                  <div className="pfi-actions">
                    {appStoreBadge('hero')}
                    {ctaLink('size_tool', 'Find your print size', 'hero', 'print-guide-cta')}
                  </div>
                </div>
                <figure className="pfi-hero-photo">
                  <CloudinaryPicture
                    prefix="products/"
                    imageKey="square-prints/1"
                    widths={WIDTH_LADDERS.products}
                    sizes="(max-width: 991px) 100vw, 440px"
                    alt="Parabo Square Prints displayed together"
                    priority
                  />
                  <figcaption>Your favorites, somewhere you can see them.</figcaption>
                </figure>
              </div>
            </Container>
          </section>

          <Container>
            <div className="pfi-body">
              <aside className="pfi-toc">
                <nav aria-label="On this page">
                  <p className="pfi-toc-label">On this page</p>
                  <ul>
                    {SECTIONS.map(({ id, label }) => (
                      <li key={id}>
                        <a href={`#${id}`} className={classNames({ active: activeId === id })}>
                          {label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
                <div className="pfi-toc-cta">
                  <p>Not sure which size?</p>
                  {arrowLink('size_tool', 'Open the size guide', 'toc')}
                </div>
              </aside>

              <div className="pfi-reading">
                <section id="short-answer" className="pfi-short-answer">
                  <p className="pfi-short-answer-label">
                    <PdpIcon name="zap" size={16} />
                    Short answer
                  </p>
                  <p className="pfi-short-answer-text">
                    You don’t need a printer. Open the Parabo Press app, add your favorites from the
                    Camera Roll, pick Square or Classic Prints, check the crop and border, then
                    order. Parabo prints them and ships them to you.
                  </p>
                </section>

                <section id="why-print" className="pfi-chapter">
                  <h2 className="pfi-h2">Why print from iPhone (not just save to camera roll)</h2>
                  <p>
                    A photo on your phone can disappear into a thousand others. A print gives it a
                    place in your day: on a desk, tucked into a journal or shared with someone you
                    love. You do not need to organize your whole library first. Start with the
                    photos you keep coming back to.
                  </p>
                </section>

                <section id="airprint-vs-lab" className="pfi-chapter">
                  <h2 className="pfi-h2">AirPrint vs a photo lab: what’s the difference?</h2>
                  <p>
                    AirPrint is for sending a photo to a compatible printer. You supply the printer,
                    paper and ink. Ordering from a photo lab is a different route: choose your
                    photos and print options, then have the finished prints delivered.
                  </p>
                  <p>
                    This guide follows that second route. With Parabo, your phone is where you
                    choose and order your prints. The printing happens at the lab.
                  </p>
                  <div className="pfi-table-wrap">
                    <table className="pfi-table pfi-compare">
                      <thead>
                        <tr>
                          <td />
                          <th scope="col">AirPrint</th>
                          <th scope="col">Photo lab (Parabo)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {AIRPRINT_ROWS.map((row) => (
                          <tr key={row.label}>
                            <th scope="row">{row.label}</th>
                            <td>{row.airprint}</td>
                            <td className="pfi-compare-lab">
                              <PdpIcon name="check" size={16} />
                              {row.lab}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="pfi-callout">
                    <div>
                      {/* Kept as an H2 so the heading outline matches the indexed page. */}
                      <h2 className="pfi-callout-title">Fastest path: the Parabo Press app</h2>
                      <p>
                        Keep the process on the device that already holds your photos. Open the
                        Parabo Press app, choose a print product and add your favorites. Square and
                        Classic Prints are a simple place to start, with thick matte paper and sets
                        of 20.
                      </p>
                    </div>
                    {appStoreBadge('app')}
                  </div>
                </section>

                <section id="how-to-order" className="pfi-chapter">
                  <h2 className="pfi-h2">Step-by-step: Camera Roll to size, border and order</h2>
                  <ol className="pfi-steps">
                    {STEPS.map((step, index) => (
                      <li key={step.id} className="pfi-step">
                        <span className="pfi-step-number" aria-hidden="true">
                          {index + 1}
                        </span>
                        <div>
                          <h3 className="pfi-h3">{step.title}</h3>
                          <p>{step.body}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </section>

                <section id="choose-your-size" className="pfi-chapter">
                  <h2 className="pfi-h2">Which size from your phone?</h2>
                  <p>
                    Choose for the space, then check the crop. Tiny squares fit journals and gift
                    tags. A 4×4 square or 4×6 rectangle works well on a desk or in a gift stack.
                    For a little more presence, try a 5.5×5.5 square or a 5×7 Classic Print.
                  </p>
                  <div className="pfi-table-wrap">
                    <table className="pfi-table pfi-sizes">
                      <thead>
                        <tr>
                          <th scope="col">Size</th>
                          <th scope="col">Product</th>
                          <th scope="col">Good for</th>
                        </tr>
                      </thead>
                      <tbody>
                        {SIZES.map(({ size, product, use, shape }) => (
                          <tr key={size}>
                            <th scope="row">
                              <span className="pfi-size-cell">
                                <span className="pfi-size-shape" aria-hidden="true">
                                  <span style={{ width: shape[0], height: shape[1] }} />
                                </span>
                                {size}
                              </span>
                            </th>
                            <td>{product}</td>
                            <td>{use}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p>
                    Our free size guide helps you match a print to where it will live. Compare the
                    dimensions and see which Parabo product fits, without uploading a photo.
                  </p>
                  {arrowLink('size_tool', 'Open the photo print sizes guide', 'size')}
                </section>

                <section id="heic-quality" className="pfi-chapter">
                  <h2 className="pfi-h2">HEIC, quality, and when to use originals</h2>
                  <p>
                    Parabo accepts HEIC, JPG and PNG. Whatever the format, start with the biggest
                    original you have. Screenshots, social-media downloads and tight crops can leave
                    you with fewer pixels to print.
                  </p>
                  <p>
                    Print quality depends on those pixels and the size you choose. For Square and
                    Classic Prints, aim for 150+ pixels per inch (PPI). Enlarging the same file
                    spreads its pixels across more paper. A blurry original will still look blurry
                    in print, even if the file is large.
                  </p>
                  <div className="pfi-do-avoid">
                    <div className="pfi-do">
                      <p className="pfi-list-title">Use</p>
                      <ul>
                        {USE_LIST.map((item) => (
                          <li key={item}>
                            <PdpIcon name="check" size={16} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="pfi-avoid">
                      <p className="pfi-list-title">Avoid</p>
                      <ul>
                        {AVOID_LIST.map((item) => (
                          <li key={item}>
                            <PdpIcon name="x" size={16} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="pfi-ppi">
                    {PPI_TARGETS.map(({ value, label }) => (
                      <div key={value}>
                        <p className="pfi-ppi-value">{value}</p>
                        <p className="pfi-ppi-label">{label}</p>
                      </div>
                    ))}
                  </div>
                  {arrowLink('size_tool', 'Compare print sizes and resolution guidance', 'quality')}
                </section>

                <section id="square-vs-classic" className="pfi-chapter">
                  <h2 className="pfi-h2">Square vs Classic for iPhone photos</h2>
                  <p>
                    Let the photo help you choose. A square crop can bring attention to a face or a
                    detail. A rectangle can leave more room for a landscape or a group. Check the
                    preview so the shape does not cut out something you want to keep.
                  </p>
                  <div className="pfi-products">
                    {PRODUCTS.map((product) => (
                      <div key={product.id} className="pfi-product">
                        <div className={`pfi-product-preview pfi-product-preview-${product.id}`}>
                          <div className="pfi-product-print">
                            <CloudinaryPicture
                              prefix="products/"
                              imageKey={product.imageKey}
                              widths={WIDTH_LADDERS.products}
                              sizes="220px"
                              alt=""
                              loading="lazy"
                            />
                          </div>
                        </div>
                        <h3 className="pfi-h3">{product.title}</h3>
                        <p className="pfi-product-sizes">{product.sizes}</p>
                        <p>{product.body}</p>
                        {arrowLink(product.id, product.cta, 'products')}
                      </div>
                    ))}
                  </div>
                  <p>
                    Both come in sets of 20 on thick matte FSC certified paper, with a white border
                    or an edge-to-edge finish.
                  </p>
                </section>

                <section id="big-print" className="pfi-chapter">
                  <h2 className="pfi-h2">Want one big print?</h2>
                  <div className="pfi-big-print">
                    <div className="pfi-scale" aria-hidden="true">
                      <span className="pfi-scale-value">3×4</span>
                      <span className="pfi-scale-unit">Feet</span>
                    </div>
                    <div>
                      <p>
                        An Engineer Print turns one photo into a 3×4 ft wall piece on extra-light
                        paper. That scale asks more of your file. Use a strong, high-resolution
                        original and aim for about 300 PPI for the best results. A file saved from
                        social media or heavily cropped may work better at a smaller size.
                      </p>
                      <p>
                        If your original has the resolution for a large print, explore Engineer
                        Prints. If you are unsure, start with the size guide and consider a smaller
                        Square or Classic Print.
                      </p>
                      <div className="pfi-links">
                        {arrowLink('engineer-prints', 'Explore Engineer prints', 'large')}
                        {arrowLink('size_tool', 'Find a smaller print size', 'large')}
                      </div>
                    </div>
                  </div>
                </section>

              </div>
            </div>
          </Container>

          <section id="faq" className="pdp-section pdp-section-green">
            <Container>
              <ProductFaq caption="printing from iPhone" items={FAQ_ITEMS} />
            </Container>
          </section>

          <section className="pfi-band">
            <Container>
              <p className="pfi-eyebrow">From your phone to your home</p>
              <h2 className="pfi-band-title">Ready to print your favorites?</h2>
              <p className="pfi-band-text">
                Choose a few photos today. Give them a place beyond your Camera Roll.
              </p>
              <div className="pfi-actions">
                {appStoreBadge('band')}
                {ctaLink('size_tool', 'Find your print size', 'band', 'print-guide-cta')}
              </div>
            </Container>
          </section>
        </article>
      </Layout>
    </>
  );
};

export default PrintPhotosFromIphonePage;
